import type { IdZonaOperacaoTerceiraPorta } from '../../dominio/didatico/oficinaTerceiraPorta'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { AjusteFerramentaPorta } from './zonasOperacaoPorta'

export interface ZonaOperacaoTerceiraPorta3D {
  id: IdZonaOperacaoTerceiraPorta
  nome: string
  posicao: [number, number, number]
  /** Inclinação recomendada da ferramenta no plano da chapa. */
  anguloGraus: number
}

export interface AvaliacaoContatoTerceiraPorta {
  zona: IdZonaOperacaoTerceiraPorta | null
  distancia: number | null
  contatoValido: boolean
  orientacaoValida: boolean
}

export type IdLadoTerceiraPorta = 'esquerdo' | 'direito'

export const TOLERANCIA_CONTATO_TERCEIRA_PORTA = 0.2
export const TOLERANCIA_ANGULO_TERCEIRA_PORTA_GRAUS = 30

/**
 * Três zonas ao longo do perímetro do vão da porta traseira — mesmo
 * footprint de `perfil.portaTraseira` usado por `zonasOperacaoPorta.ts` —,
 * já que a terceira porta é tratada como essa mesma região sem dobradiça,
 * trava ou vidro reais, a ser cortada.
 */
export function zonasOperacaoTerceiraPorta(
  perfil: PerfilVeiculo,
  lado: IdLadoTerceiraPorta,
): readonly ZonaOperacaoTerceiraPorta3D[] {
  const sinal = lado === 'esquerdo' ? 1 : -1
  const x = sinal * (perfil.largura / 2 + 0.055)
  const yBaixo = perfil.topoSoleiraY
  const yCintura = perfil.cinturaY
  const meioY = (yBaixo + yCintura) / 2
  const faixa = perfil.portaTraseira
  const meioZ = (faixa.zDianteiro + faixa.zTraseiro) / 2

  return [
    { id: 'superior', nome: 'Lado superior', posicao: [x, yCintura, meioZ], anguloGraus: 90 },
    { id: 'inferior', nome: 'Lado inferior', posicao: [x, yBaixo, meioZ], anguloGraus: 90 },
    { id: 'traseiro', nome: 'Lado traseiro', posicao: [x, meioY, faixa.zTraseiro], anguloGraus: 0 },
  ]
}

export function zonaOperacaoTerceiraPortaPorId(
  perfil: PerfilVeiculo,
  lado: IdLadoTerceiraPorta,
  idZona: IdZonaOperacaoTerceiraPorta | null,
): ZonaOperacaoTerceiraPorta3D | null {
  if (!idZona) return null
  return zonasOperacaoTerceiraPorta(perfil, lado).find((zona) => zona.id === idZona) ?? null
}

export function posicaoFerramentaNaTerceiraPorta(
  perfil: PerfilVeiculo,
  lado: IdLadoTerceiraPorta,
  idZona: IdZonaOperacaoTerceiraPorta | null,
  ajuste: AjusteFerramentaPorta,
): [number, number, number] | null {
  const zona = zonaOperacaoTerceiraPortaPorId(perfil, lado, idZona)
  if (!zona) return null
  return [zona.posicao[0], zona.posicao[1] + ajuste.deslocamentoY, zona.posicao[2] + ajuste.deslocamentoZ]
}

/** Avalia contato e orientação sem depender do marcador originalmente escolhido. */
export function avaliarContatoFerramentaNaTerceiraPorta(
  perfil: PerfilVeiculo,
  lado: IdLadoTerceiraPorta,
  posicao: [number, number, number] | null,
  anguloGraus: number,
): AvaliacaoContatoTerceiraPorta {
  if (!posicao) return { zona: null, distancia: null, contatoValido: false, orientacaoValida: false }

  const zonas = zonasOperacaoTerceiraPorta(perfil, lado)
  const ordenadas = zonas
    .map((zona) => ({
      zona,
      distancia: Math.hypot(posicao[1] - zona.posicao[1], posicao[2] - zona.posicao[2]),
    }))
    .sort((a, b) => a.distancia - b.distancia)
  const proxima = ordenadas[0]
  if (!proxima || proxima.distancia > TOLERANCIA_CONTATO_TERCEIRA_PORTA) {
    return { zona: null, distancia: proxima?.distancia ?? null, contatoValido: false, orientacaoValida: false }
  }

  const diferencaBruta = Math.abs(anguloGraus - proxima.zona.anguloGraus) % 180
  const diferenca = Math.min(diferencaBruta, 180 - diferencaBruta)
  return {
    zona: proxima.zona.id,
    distancia: proxima.distancia,
    contatoValido: true,
    orientacaoValida: diferenca <= TOLERANCIA_ANGULO_TERCEIRA_PORTA_GRAUS,
  }
}
